import React from 'react'
import { PRICING_DATA_THEME } from 'modules/billing/pricing-theme'

import { TakaLogo } from '../../../components/TakaLogo'

interface BaseLoginPageLayoutProps {
  children: React.ReactNode
}

/**
 * Login page layout on the Taka design system.
 *
 * The left hero is the marketing-canonical lockup — same copy and
 * shape as the website hero so the brand reads as one surface across
 * landing → login. Per-flow chrome (`heroHeadline`, `heroSubheadline`,
 * `heroContent`, `gradient`, `logoSection`) lives in `LoginLayoutConfig`
 * for legacy purple LPs and will re-enter as each LP migrates.
 *
 * Brand-locked to dark via `data-theme="dark"` regardless of the user's
 * chosen app theme — same posture as paywall and billing (`PRICING_DATA_THEME`).
 */
export const BaseLoginPageLayout: React.FC<BaseLoginPageLayoutProps> = ({ children }) => {
  return (
    <div
      className="taka-dark relative min-h-screen w-full overflow-hidden bg-bg text-paper"
      data-theme={PRICING_DATA_THEME}
    >
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
        <symbol id="taka-icn-logo" viewBox="0 0 42 42">
          <path
            fill="currentColor"
            d="M23.8926 35.0578C23.8926 35.5782 23.4707 36 22.9504 36H14.1901C13.6698 36 13.2479 35.5782 13.2479 35.0578V20.7584C13.2479 20.238 12.8261 19.8162 12.3058 19.8162H7.94218C7.42183 19.8162 7 19.3943 7 18.874V10.501C7 9.98065 7.42183 9.55882 7.94218 9.55882H11.6281V14.3152C11.6281 14.8355 12.0499 15.2574 12.5703 15.2574H16.9339C17.4542 15.2574 17.876 15.6792 17.876 16.1995V30.499C17.876 31.0193 18.2979 31.4412 18.8182 31.4412H23.8926V35.0578Z"
          />
          <path
            fill="currentColor"
            d="M35 18.874C35 19.3943 34.5782 19.8162 34.0578 19.8162H29.4628C28.9425 19.8162 28.5207 20.238 28.5207 20.7584V30.499C28.5207 31.0193 28.0988 31.4412 27.5785 31.4412H23.8926V16.1995C23.8926 15.6792 24.3144 15.2574 24.8347 15.2574H29.4297C29.9501 15.2574 30.3719 14.8355 30.3719 14.3152V10.501C30.3719 9.98065 29.9501 9.55882 29.4297 9.55882H11.6281V5.94218C11.6281 5.42183 12.0499 5 12.5703 5H34.0578C34.5782 5 35 5.42183 35 5.94218V18.874Z"
          />
        </symbol>
        <symbol id="taka-icn-check" viewBox="0 0 16 16">
          <polyline points="3.5 8.5 6.8 11.6 12.5 5" />
        </symbol>
      </svg>

      <div className="grid-bg pointer-events-none absolute inset-0 opacity-60" aria-hidden />
      <div
        className="glow"
        style={{ top: '-220px', left: '8%', width: '720px', height: '520px' }}
        aria-hidden
      />

      <a
        href="/"
        className="reveal d-1 absolute top-6 left-6 z-20 flex items-center text-acid lg:top-8 lg:left-10"
        aria-label="Taka"
      >
        <TakaLogo className="h-7 w-auto" />
      </a>

      <div className="mono absolute bottom-6 left-10 z-20 hidden text-[10px] tracking-[0.2em] text-paper-faint uppercase lg:block">
        © 2026 a monday.com product
      </div>

      <div className="relative z-10 grid min-h-screen grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
        {/* LEFT: editorial hero, desktop only */}
        <div className="hidden lg:flex lg:flex-col lg:justify-center lg:px-14 lg:py-24 xl:px-20">
          <div className="reveal d-2 mb-7 flex items-center gap-3">
            <span className="pill">
              <span className="liveDot" />
              LIVE · <span className="num-em ml-0.5">4,812</span> posts shipped this week
            </span>
          </div>

          <h1 className="h1-mega reveal d-3 max-w-[680px] text-[clamp(44px,5.2vw,80px)] font-semibold">
            <div className="text-paper">Best-in-class content.</div>
            <div className="h1-serif-row text-paper-dim">
              <span className="serif-it">For every channel.</span>
            </div>
            <div className="text-acid">On autopilot.</div>
          </h1>

          <p className="lede reveal d-4 mt-7 max-w-[520px]">
            Taka writes, designs, and schedules expert-quality social content for your business. So
            you ship more, and{' '}
            <strong className="font-semibold text-paper">win&nbsp;attention.</strong>
          </p>

          <div className="reveal d-5 feature-list mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px]">
            <FeatureCheck label="60-second setup" />
            <FeatureCheck label="Free trial" />
            <FeatureCheck label="Cancel anytime" />
          </div>
        </div>

        {/* RIGHT: form column */}
        <div className="flex min-h-screen items-center justify-center px-5 py-24 sm:px-6 lg:min-h-0 lg:py-12">
          <div className="w-full max-w-[420px]">{children}</div>
        </div>
      </div>
    </div>
  )
}

const FeatureCheck: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex items-center gap-2 whitespace-nowrap">
    <span className="check-pill">
      <svg aria-hidden>
        <use href="#taka-icn-check" />
      </svg>
    </span>
    {label}
  </span>
)
